// src/pages/AdminPromoManagerPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  adminLogin,
  adminCreatePromo,
  adminCreateAuto,
  adminListPromos,
  adminEnable,
  adminDisable,
  getAdminToken,
  setAdminToken,
  clearAdminToken,
} from "../lib/adminApi";

type PromoRow = {
  code: string;
  plan: string;
  max_uses: number | null;
  uses_count: number;
  expires_at: string | null;
  is_active: boolean;
  is_expired?: boolean; // ✅ backend provides this
  created_at: string | null;
};

const plans = ["STARTER", "GROWTH", "INSIGHT", "PREMIUM_UNLOCK"];

export default function AdminPromoManagerPage() {
  const [token, setToken] = useState("");

  // Login form
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [loginState, setLoginState] = useState<
    "idle" | "loading" | "error" | "ok"
  >("idle");
  const [loginError, setLoginError] = useState("");

  // Manual create
  const [createCode, setCreateCode] = useState(""); // ✅ NEW (required by backend)
  const [createPlan, setCreatePlan] = useState("PREMIUM_UNLOCK");
  const [createMaxUses, setCreateMaxUses] = useState("5");
  const [createValidDays, setCreateValidDays] = useState("3");
  const [createState, setCreateState] = useState<"idle" | "loading">("idle");

  // Auto create
  const [chartId, setChartId] = useState("");
  const [autoSpec, setAutoSpec] = useState("PP,M1,D3");
  const [autoState, setAutoState] = useState<"idle" | "loading">("idle");

  // List
  const [listState, setListState] = useState<"idle" | "loading">("idle");
  const [rows, setRows] = useState<PromoRow[]>([]);
  const [tableError, setTableError] = useState("");

  // Last created
  const [lastCreated, setLastCreated] = useState<any>(null);

  // Filters (Frontend only)
  const [filters, setFilters] = useState({
    usableOnly: true, // ✅ default ON
    includeDisabled: false,
    includeExpired: false,
    includeFullyUsed: false,
    search: "",
  });

  const isLoggedIn = Boolean(token);

  useEffect(() => {
    const t = getAdminToken();
    if (t) setToken(t);
  }, []);

const prettyTime = (iso?: string | null) => {
  if (!iso) return "—";

  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;

  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};
  const onLogout = () => {
    clearAdminToken();
    setToken("");
    setPassword("");
    setLoginState("idle");
    setLoginError("");
    setRows([]);
    setLastCreated(null);
  };

  const fetchList = async () => {
    setListState("loading");
    setTableError("");

    try {
      const { res, data } = await adminListPromos();

      if (res.status === 401 || data?.error === "TOKEN_EXPIRED") {
        onLogout();
        return;
      }

      // backend may return: { ok:true, items:[...] } OR { ok:true, codes:[...] }
      const list = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.codes)
        ? data.codes
        : null;

      if (!data?.ok || !list) {
        setTableError(data?.error || "Failed to load promo list.");
        setListState("idle");
        return;
      }

      setRows(list);
      setListState("idle");
    } catch {
      setTableError("Network error while loading promo list.");
      setListState("idle");
    }
  };


  const handleRefresh = () => {
    setLastCreated(null);
    setChartId("");
    fetchList();
  };

  // Auto-load list after login
  useEffect(() => {
    if (!isLoggedIn) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const onLogin = async () => {
    const u = username.trim();
    const p = password;

    if (!u || !p) {
      setLoginState("error");
      setLoginError("Enter username and password.");
      return;
    }

    setLoginState("loading");
    setLoginError("");

    try {
      const { data } = await adminLogin(u, p);

      if (!data?.ok || !data?.access_token) {
        setLoginState("error");
        setLoginError(data?.error || "Login failed.");
        return;
      }

      // success
      setAdminToken(data.access_token);
      setToken(data.access_token);

      setLoginState("ok");
      setPassword("");
    } catch {
      setLoginState("error");
      setLoginError("Network error during login.");
    }
  };

  const onCreateManual = async () => {
    setCreateState("loading");
    setLastCreated(null);

    const code = createCode.trim(); // ✅ required
    if (!code) {
      alert("CODE_REQUIRED");
      setCreateState("idle");
      return;
    }

    const maxUsesNum = Number(createMaxUses);
    const validDaysNum = Number(createValidDays);

    const payload: any = {
      code,
      plan: createPlan,
      max_uses: Number.isFinite(maxUsesNum) ? maxUsesNum : 1,
      valid_days: Number.isFinite(validDaysNum) ? validDaysNum : 1,
    };

    try {
      const { res, data } = await adminCreatePromo(payload);

      if (res.status === 401 || data?.error === "TOKEN_EXPIRED") {
        onLogout();
        return;
      }

      if (!data?.ok) {
        alert(data?.error || "Failed to create promo.");
        setCreateState("idle");
        return;
      }

      setLastCreated(data);
      setCreateState("idle");
      setCreateCode(""); // clear on success
      fetchList();
    } catch {
      alert("Network error while creating promo.");
      setCreateState("idle");
    }
  };

  const onCreateAuto = async () => {
    const spec = autoSpec.trim();
    if (!spec) return;

    setAutoState("loading");
    setLastCreated(null);

    try {
      const { res, data } = await adminCreateAuto(spec);

      if (res.status === 401 || data?.error === "TOKEN_EXPIRED") {
        onLogout();
        return;
      }

      if (!data?.ok) {
        alert(data?.error || "Failed to generate promo.");
        setAutoState("idle");
        return;
      }

      setLastCreated(data);
      setChartId("");
      setAutoState("idle");
      fetchList();
    } catch {
      alert("Network error while generating promo.");
      setAutoState("idle");
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Copied!");
    } catch {
      alert("Copy failed.");
    }
  };

  const toggleEnable = async (code: string, enable: boolean) => {
    try {
      const fn = enable ? adminEnable : adminDisable;
      const { res, data } = await fn(code);

      if (res.status === 401 || data?.error === "TOKEN_EXPIRED") {
        onLogout();
        return;
      }

      if (!data?.ok) {
        alert(data?.error || "Action failed.");
        return;
      }

      fetchList();
    } catch {
      alert("Network error.");
    }
  };

  // -----------------------------
  // Helpers: Fully used / status
  // -----------------------------
  const isFullyUsed = (p: PromoRow) => {
    const max = typeof p.max_uses === "number" ? p.max_uses : 0;
    const used = typeof p.uses_count === "number" ? p.uses_count : 0;
    return max > 0 && used >= max;
  };

  const isExpired = (p: PromoRow) => {
    // backend gives is_expired, but fallback to expires_at parse
    if (typeof p.is_expired === "boolean") return p.is_expired;

    if (!p.expires_at) return false;
    const t = new Date(p.expires_at).getTime();
    if (!Number.isFinite(t)) return false;
    return Date.now() > t;
  };

  const isUsable = (p: PromoRow) => {
    return p.is_active && !isExpired(p) && !isFullyUsed(p);
  };

  const getPromoStatus = (p: PromoRow) => {
    if (isExpired(p)) return "Expired";
    if (isFullyUsed(p)) return "Fully Used";
    if (!p.is_active) return "Disabled";
    return "Active";
  };

  const promoRank = (p: PromoRow) => {
    // lower = higher priority
    if (isUsable(p)) return 1;
    if (!p.is_active) return 2;
    if (isFullyUsed(p)) return 3;
    if (isExpired(p)) return 4;
    return 5;
  };

  // -----------------------------
  // Stats bar
  // -----------------------------
  const stats = useMemo(() => {
    let total = rows.length;
    let usable = 0;
    let disabled = 0;
    let expired = 0;
    let fullyUsed = 0;

    for (const p of rows) {
      const fu = isFullyUsed(p);
      const ex = isExpired(p);

      if (ex) expired++;
      if (!p.is_active) disabled++;
      if (fu) fullyUsed++;

      if (p.is_active && !ex && !fu) usable++;
    }

    return { total, usable, disabled, expired, fullyUsed };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rows)]);

  // -----------------------------
  // Filter + Search + Sort
  // -----------------------------
  const filteredRows = useMemo(() => {
    const search = filters.search.trim().toLowerCase();

    const out = (rows || [])
      .filter((p) => {
        const fu = isFullyUsed(p);
        const ex = isExpired(p);
        const usableNow = p.is_active && !ex && !fu;

        // default view
        if (filters.usableOnly) return usableNow;

        // custom view
        if (!filters.includeDisabled && !p.is_active) return false;
        if (!filters.includeExpired && ex) return false;
        if (!filters.includeFullyUsed && fu) return false;

        return true;
      })
      .filter((p) => {
        if (!search) return true;
        return String(p.code || "")
          .toLowerCase()
          .includes(search);
      })
      .sort((a, b) => {
        const ra = promoRank(a);
        const rb = promoRank(b);
        if (ra !== rb) return ra - rb;

        // newest first
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });

    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rows), JSON.stringify(filters)]);

  // -----------------------------
  // UI
  // -----------------------------
  const headerStyle = {
    minHeight: "100vh",
    background: "#020617",
    color: "#f9fafb",
    padding: 20,
    maxWidth: 1200,
    margin: "16px auto",
    boxSizing: "border-box" as const,
  };

  const cardStyle = {
    background: "rgba(15,23,42,0.95)",
    border: "2px solid rgba(148,163,184,0.25)",
    borderRadius: 12,
    padding: 14,
  };

  const StatCard = ({ label, value }: any) => {
    return (
      <div
        style={{
          borderRadius: 14,
          border: "1px solid rgba(255,255,255,0.10)",
          background: "rgba(255,255,255,0.04)",
          padding: "12px 14px",
        }}
      >
        <div style={{ fontSize: 12, color: "#9ca3af", fontWeight: 700 }}>
          {label}
        </div>
        <div style={{ fontSize: 22, fontWeight: 950, color: "#f8fafc" }}>
          {value}
        </div>
      </div>
    );
  };

  return (
    <div style={headerStyle}>
      <div style={{ fontSize: 20, fontWeight: 900, color: "#fbbf24" }}>
        Promo Manager (Admin)
      </div>
      <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 4 }}>
        Hidden route · JWT protected · For internal testing
      </div>

      <div style={{ height: 14 }} />

      {!isLoggedIn ? (
        <div style={{ ...cardStyle, maxWidth: 520 }}>
          <div style={{ fontWeight: 800, marginBottom: 10 }}>Admin Login</div>

          <div style={{ display: "grid", gap: 10 }}>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid rgba(148,163,184,0.22)",
                background: "rgba(2,6,23,0.55)",
                color: "#e5e7eb",
                fontSize: 14,
                outline: "none",
              }}
            />

            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              type="password"
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid rgba(148,163,184,0.22)",
                background: "rgba(2,6,23,0.55)",
                color: "#e5e7eb",
                fontSize: 14,
                outline: "none",
              }}
            />

            <button
              type="button"
              onClick={onLogin}
              disabled={loginState === "loading"}
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid rgba(148,163,184,0.25)",
                background:
                  "linear-gradient(90deg, rgba(249,115,22,0.18), rgba(250,204,21,0.12))",
                color: "#f9fafb",
                cursor: loginState === "loading" ? "not-allowed" : "pointer",
                fontSize: 14,
                fontWeight: 800,
              }}
            >
              {loginState === "loading" ? "Logging in…" : "Login"}
            </button>

            {loginState === "error" ? (
              <div style={{ color: "#fca5a5", fontSize: 13 }}>
                {loginError}
              </div>
            ) : (
              <div style={{ color: "#64748b", fontSize: 12 }}>
                Route is hidden, but security is JWT-based.
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 14 }}>
          {/* Top bar */}
          <div style={cardStyle}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontSize: 13, color: "#9ca3af" }}>
                Logged in · Token stored in <b>localStorage</b>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={listState === "loading"}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 10,
                    border: "1px solid rgba(148,163,184,0.25)",
                    background: "rgba(148,163,184,0.08)",
                    color: "#e5e7eb",
                    cursor:
                      listState === "loading" ? "not-allowed" : "pointer",
                    fontSize: 13,
                    fontWeight: 750,
                  }}
                >
                  {listState === "loading" ? "Refreshing…" : "Refresh List"}
                </button>

                <button
                  type="button"
                  onClick={onLogout}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 10,
                    border: "1px solid rgba(239,68,68,0.25)",
                    background: "rgba(239,68,68,0.10)",
                    color: "#fecaca",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 750,
                  }}
                >
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Create Manual */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 900, marginBottom: 10 }}>
              A) Create Manual Promo
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <input
                value={createCode}
                onChange={(e) => setCreateCode(e.target.value)}
                placeholder="code (example: TEST123)"
                style={{
                  width: 180,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(148,163,184,0.22)",
                  background: "rgba(2,6,23,0.55)",
                  color: "#e5e7eb",
                }}
              />

              <select
                value={createPlan}
                onChange={(e) => setCreatePlan(e.target.value)}
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(148,163,184,0.22)",
                  background: "rgba(2,6,23,0.55)",
                  color: "#e5e7eb",
                }}
              >
                {plans.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>

              <input
                value={createMaxUses}
                onChange={(e) => setCreateMaxUses(e.target.value)}
                placeholder="max_uses"
                style={{
                  width: 120,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(148,163,184,0.22)",
                  background: "rgba(2,6,23,0.55)",
                  color: "#e5e7eb",
                }}
              />

              <input
                value={createValidDays}
                onChange={(e) => setCreateValidDays(e.target.value)}
                placeholder="valid_days"
                style={{
                  width: 120,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(148,163,184,0.22)",
                  background: "rgba(2,6,23,0.55)",
                  color: "#e5e7eb",
                }}
              />

              <button
                type="button"
                onClick={onCreateManual}
                disabled={createState === "loading"}
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(148,163,184,0.25)",
                  background:
                    "linear-gradient(90deg, rgba(249,115,22,0.18), rgba(250,204,21,0.12))",
                  color: "#f9fafb",
                  cursor: createState === "loading" ? "not-allowed" : "pointer",
                  fontWeight: 850,
                }}
              >
                {createState === "loading" ? "Creating…" : "Create"}
              </button>
            </div>
          </div>

          {/* Create Auto */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 900, marginBottom: 10 }}>
              B) Create Auto Promo (Spec)
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <input
                value={autoSpec}
                onChange={(e) => setAutoSpec(e.target.value)}
                placeholder="spec (example: PP,M1,D3)"
                style={{
                  width: 260,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(148,163,184,0.22)",
                  background: "rgba(2,6,23,0.55)",
                  color: "#e5e7eb",
                }}
              />

              <button
                type="button"
                onClick={onCreateAuto}
                disabled={autoState === "loading"}
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(148,163,184,0.25)",
                  background:
                    "linear-gradient(90deg, rgba(249,115,22,0.18), rgba(250,204,21,0.12))",
                  color: "#f9fafb",
                  cursor: autoState === "loading" ? "not-allowed" : "pointer",
                  fontWeight: 850,
                }}
              >
                {autoState === "loading" ? "Generating…" : "Generate"}
              </button>
            </div>
          </div>

          {/* Last created */}
          {lastCreated?.code ? (

            <div style={cardStyle}>
              <div style={{ fontWeight: 900, marginBottom: 10 }}>
                ✅ Last Created
              </div>
              <button type="button" class="btn-close" aria-label="Close"></button>

          <div style={{ fontSize: 13, color: "#e5e7eb" }}>
            <b style={{ color: "#fbbf24" }}>{lastCreated.code}</b> ·{" "}
            {lastCreated.plan} · Expires: {prettyTime(lastCreated.expires_at)}
          </div>

          {/* Chart ID Input */}
          <div style={{ marginTop: 14 }}>
            <input
              type="text"
              placeholder="Enter chart_id"
              value={chartId}
              onChange={(e) => setChartId(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 10,
                border: "1px solid rgba(148,163,184,0.25)",
                background: "#0f172a",
                color: "#e5e7eb",
                fontSize: 13,
              }}
            />
          </div>

          {/* Activation Link */}
          {chartId && (
            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  fontSize: 12,
                  marginBottom: 4,
                  opacity: 0.8,
                }}
              >
                Activation Link
              </div>

              <input
                type="text"
                readOnly
                value={`${window.location.origin}/unlock?code=${lastCreated.code}&chart_id=${chartId}`}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid rgba(148,163,184,0.25)",
                  background: "#020617",
                  color: "#fbbf24",
                  fontSize: 12,
                }}
              />
            </div>
          )}

          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={() => copy(lastCreated.code)}
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                border: "1px solid rgba(148,163,184,0.25)",
                background: "rgba(148,163,184,0.08)",
                color: "#e5e7eb",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 750,
              }}
            >
              Copy Code
            </button>

            {chartId && (
              <button
                type="button"
                onClick={() =>
                  copy(
                    `${window.location.origin}/unlock?code=${lastCreated.code}&chart_id=${chartId}`
                  )
                }
                style={{
                  padding: "8px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(251,191,36,0.35)",
                  background: "rgba(251,191,36,0.1)",
                  color: "#fbbf24",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 800,
                }}
              >
                Copy Activation Link
              </button>
            )}
          </div>

            </div>
          ) : null}


          {/* Stats + Filters + Table */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 900, marginBottom: 10 }}>
              C) Promo List
            </div>

            {/* Stats bar */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                gap: 10,
                marginBottom: 14,
              }}
            >
              <StatCard label="Usable" value={stats.usable} />
              <StatCard label="Disabled" value={stats.disabled} />
              <StatCard label="Expired" value={stats.expired} />
              <StatCard label="Fully Used" value={stats.fullyUsed} />
              <StatCard label="Total" value={stats.total} />
            </div>

            {/* Filters */}
            <div
              style={{
                display: "flex",
                gap: 14,
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                <label style={{ display: "flex", gap: 8, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={filters.usableOnly}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setFilters((prev) => ({
                        ...prev,
                        usableOnly: on,
                        // if usableOnly ON, force others OFF (clean UX)
                        includeDisabled: on ? false : prev.includeDisabled,
                        includeExpired: on ? false : prev.includeExpired,
                        includeFullyUsed: on ? false : prev.includeFullyUsed,
                      }));
                    }}
                  />
                  <span style={{ fontSize: 13, color: "#e5e7eb" }}>
                    Active usable only
                  </span>
                </label>

                <label style={{ display: "flex", gap: 8, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={filters.includeDisabled}
                    disabled={filters.usableOnly}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        includeDisabled: e.target.checked,
                      }))
                    }
                  />
                  <span style={{ fontSize: 13, color: "#9ca3af" }}>
                    Include Disabled
                  </span>
                </label>

                <label style={{ display: "flex", gap: 8, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={filters.includeExpired}
                    disabled={filters.usableOnly}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        includeExpired: e.target.checked,
                      }))
                    }
                  />
                  <span style={{ fontSize: 13, color: "#9ca3af" }}>
                    Include Expired
                  </span>
                </label>

                <label style={{ display: "flex", gap: 8, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={filters.includeFullyUsed}
                    disabled={filters.usableOnly}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        includeFullyUsed: e.target.checked,
                      }))
                    }
                  />
                  <span style={{ fontSize: 13, color: "#9ca3af" }}>
                    Include Fully Used
                  </span>
                </label>
              </div>

              {/* Search */}
              <input
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
                placeholder="Search code…"
                style={{
                  width: 240,
                  padding: "9px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(148,163,184,0.22)",
                  background: "rgba(2,6,23,0.55)",
                  color: "#e5e7eb",
                  outline: "none",
                  fontSize: 13,
                }}
              />
            </div>

            {tableError ? (
              <div style={{ color: "#fca5a5", fontSize: 13, marginBottom: 10 }}>
                {tableError}
              </div>
            ) : null}

            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr style={{ color: "#9ca3af", textAlign: "left" }}>
                    <th style={{ padding: 8 }}>Code</th>
                    <th style={{ padding: 8 }}>Plan</th>
                    <th style={{ padding: 8 }}>Max</th>
                    <th style={{ padding: 8 }}>Used</th>
                    <th style={{ padding: 8 }}>Expires</th>
                    <th style={{ padding: 8 }}>Status</th>
                    <th style={{ padding: 8 }}>Created</th>
                    <th style={{ padding: 8 }}>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredRows.map((r) => {
                    const status = getPromoStatus(r);

                    return (
                      <tr
                        key={r.code}
                        style={{
                          borderTop: "1px solid rgba(148,163,184,0.12)",
                        }}
                      >
                        <td style={{ padding: 8 }}>
                          <span style={{ fontWeight: 900, color: "#fbbf24" }}>
                            {r.code}
                          </span>
                          <div style={{ marginTop: 6 }}>
                            <button
                              type="button"
                              onClick={() => copy(r.code)}
                              style={{
                                padding: "4px 8px",
                                borderRadius: 8,
                                border: "1px solid rgba(148,163,184,0.18)",
                                background: "rgba(148,163,184,0.06)",
                                color: "#e5e7eb",
                                cursor: "pointer",
                                fontSize: 11,
                              }}
                            >
                              Copy
                            </button>
                          </div>
                        </td>

                        <td style={{ padding: 8 }}>{r.plan}</td>

                        <td style={{ padding: 8 }}>
                          {r.max_uses === 0 || r.max_uses === null
                            ? "—"
                            : r.max_uses}
                        </td>

                        <td style={{ padding: 8 }}>{r.uses_count}</td>

                        <td style={{ padding: 8 }}>{prettyTime(r.expires_at)}</td>

                        <td style={{ padding: 8 }}>
                          <span
                            style={{
                              padding: "4px 10px",
                              borderRadius: 999,
                              border: "1px solid rgba(148,163,184,0.16)",
                              background: "rgba(148,163,184,0.06)",
                              color:
                                status === "Active"
                                  ? "#86efac"
                                  : status === "Disabled"
                                  ? "#fca5a5"
                                  : status === "Fully Used"
                                  ? "#fde68a"
                                  : "#cbd5e1",
                              fontWeight: 800,
                              fontSize: 11,
                            }}
                          >
                            {status}
                          </span>
                        </td>

                        <td style={{ padding: 8 }}>
                          {prettyTime(r.created_at)}
                        </td>

                        <td style={{ padding: 8 }}>
                          {r.is_active ? (
                            <button
                              type="button"
                              onClick={() => toggleEnable(r.code, false)}
                              style={{
                                padding: "6px 10px",
                                borderRadius: 10,
                                border: "1px solid rgba(239,68,68,0.25)",
                                background: "rgba(239,68,68,0.10)",
                                color: "#fecaca",
                                cursor: "pointer",
                                fontSize: 12,
                                fontWeight: 750,
                              }}
                            >
                              Disable
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => toggleEnable(r.code, true)}
                              style={{
                                padding: "6px 10px",
                                borderRadius: 10,
                                border: "1px solid rgba(34,197,94,0.25)",
                                background: "rgba(34,197,94,0.10)",
                                color: "#bbf7d0",
                                cursor: "pointer",
                                fontSize: 12,
                                fontWeight: 750,
                              }}
                            >
                              Enable
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding: 10, color: "#64748b" }}>
                        No promos found for current filters.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
